<template>
  <div class="Wdgt-Search" @click.self="toClose()">
    <div class="Wdgt-Search__wrapper Anim-Call-Widget-Search__wrapper">
      <div class="Wdgt-Search__hdr">
        <div class="Wdgt-Search__input-box" @click="toFocus">
          <SearchIcon class="Wdgt-Search__search-icon"/>
          <input class="Wdgt-Search__input" ref="inputField" v-model="query" @input="toStartSearch" placeholder="Поиск">
        </div>
        <div class="Wdgt-Search__btns-group">
          <button class="Wdgt-Search__clear-btn" @click="resetSearchState" v-if="get_isEmptyQuery">
            <EraserIcon class="Wdgt-Search__eraser-icon"/>
            Стереть
          </button>
          <button class="Wdgt-Search__close-btn" @click="toClose">
            <CrossThinIcon/>
          </button>
        </div>
      </div>
      <div class="Wdgt-Search__body-scroll-box" :class="get_gradStyles">
        <div class="Wdgt-Search__body-scroll" @scroll="toCheckScroll" ref="scrollBox">
          <div class="Wdgt-Search__trending-scroll-box" v-if="get_coins.length > 0">
            <SectionTitle class="Wdgt-Search__trending-ttl" :iconName="'trend'" :prefix="'24ч'" v-if="!query">Трендовые монеты</SectionTitle>
            <div class="Wdgt-Search__trending-scroll">
              <TrendingCoins class="Wdgt-Search__trending" :trending-coins="get_coins" :isLoading="isLoadingData" :isSearchResult="get_isEmptyQuery"/>
            </div>
          </div>
          <MarketNow
            class="Wdgt-Search__market"
            v-if="!query"
            :isLoading="isLoadingData"
            :topGainCoins="dataTopGainCoins"
            :topLossCoins="dataTopLossCoins"
            :trendingNftCollections="dataTrendingNftCollections"
          />
          <div class="Wdgt-Search__results" v-if="query">
            <div class="Wdgt-Search__results-ldr" v-if="isLoadingData">
              <LoaderCircle :color="'var(--circle-loader)'" :size="32"/>
            </div>
            <ResultOutput :results="outputResults" v-else/>
            <div v-if="!get_isEmptyBlockShow && !isLoadingData && nextPageUrl" class="Wdgt-Search__results__btn-wrap">
              <p v-if="!isLoadingNextPage" @click="loadNextPage" class="Wdgt-Search__results__btn">Загрузить еще</p>
              <LoaderCircle v-else :color="'var(--circle-loader)'" :size="32" class="Wdgt-Search__results__btn-load"/>
            </div>
            <p class="Wdgt-Search__no-results" v-if="get_isEmptyBlockShow">{{ get_emptyText }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {onMounted, onUnmounted, ref, computed, nextTick} from 'vue';
import {useStore} from 'vuex';
import {debounce} from 'lodash';
import {backendUrl} from "@shared/constants/urls/appUrls";
import {getMarketOverview} from './api/getMarketOverview.js';
import {fetchSearchResults, fetchCoinSearchResults, fetchMarketSearchResults, fetchNextPage} from './api/search.js';
import DROPDOWN_NAMES from '@shared/constants/dropdowns/dropdownsName.js';
//-------------------------------
import SearchIcon from '@svg/SearchIcon.vue';
import EraserIcon from '@svg/EraserIcon.vue';
import CrossThinIcon from '@svg/CrossThinIcon.vue';
import LoaderCircle from '@shared/UI/Loaders/LoaderCircle/LoaderCircle.vue';
import TrendingCoins from './components/TrendingCoins/TrendingCoins.vue';
import SectionTitle from './details/SectionTitle/SectionTitle.vue';
import MarketNow from './components/MarketNow/MarketNow.vue';
import ResultOutput from './components/ResultsOutput/ResultOutput.vue';
import ButtonMain from "@ui/Buttons/ButtonMain/ButtonMain.vue";
//-------------------------------
const store = useStore();
//-------------------------------
const DEBOUNCE_TIME = 500;
const POPUP_NAME = DROPDOWN_NAMES.search;
//-------------------------------
const inputField = ref(null);
const scrollBox = ref(null);
const query = ref('');
const isLoadingData = ref(false);
const isLoadingCoins = ref(false);
const isLoadingResult = ref(false);
const isScrollTop = ref(false);
const isScrollBottom = ref(false);
const outputResults = ref([]);
const dataResultCoins = ref([]);
const dataTrendingCoins = ref([]);
const dataTopGainCoins = ref([]);
const dataTopLossCoins = ref([]);
const dataTrendingNftCollections = ref([]);
const nextPageUrl = ref(null);
const isLoadingNextPage = ref(false);
//-------------------------------
onMounted(async () => {
  toAddListeners();
  toFocus();
  await nextTick();
  toCheckScroll();
  initMarketOverview();
});
onUnmounted(() => {
  toRemoveListeners();
});
//-------------------------------
//Пустой запрос?
const get_isEmptyQuery = computed(() => query.value.length > 0);
//Отображать ли градиент при скролле
const get_gradStyles = computed(() => {
  return `
      ${isScrollTop.value ? '' : 'Wdgt-Search__body-scroll-box--top'}
      ${isScrollBottom.value ? '' : 'Wdgt-Search__body-scroll-box--bottom'}
    `
});
//Раздел, из которого будут отображаться монеты
const get_coins = computed(() => query.value ? dataResultCoins.value : dataTrendingCoins.value);
//Заглушка для пустого результата поиска
const get_isEmptyBlockShow = computed(() => {
  return query.value && outputResults.value.length === 0 && get_coins.value.length === 0 && !isLoadingData.value;
})
//Выбрать класс для отображения границы
const get_coinsBoxClass = computed(() => {
  return query.value && get_coins.value.length > 0 ? 'Wdgt-Search__results-coin--border' : '';
});
//Текст для заглушки
const get_emptyText = computed(() => `По запросу '${query.value}' ничего не найдено`);
//-------------------------------
//Начать процесс поиска
const toStartSearch = () => {
  toChangeLoaderVisible(true);
  toSearch();
}
//Запустить поиск
const toSearch = debounce(async () => {
  if (query.value.trim() === '') {
    initMarketOverview();
    return;
  }

  const fetchSearchResultsPromise = fetchSearchResults(query.value)
    .then((searchData) => {
      outputResults.value = searchData.results;
      nextPageUrl.value = searchData.next;
    })
    .catch((error) => {
      console.error('Error fetching search results:', error);
    });

  const fetchCoinSearchResultsPromise = fetchCoinSearchResults(query.value)
    .then((coinSearchData) => {
      const topThreeCurrencyIds = coinSearchData.slice(0, 3).map((market) => market.currencyId);
      return fetchMarketSearchResults(topThreeCurrencyIds)
        .then((marketDetails) => {
          const marketDetailsMap = marketDetails.reduce((acc, market) => {
            acc[market.currencyId] = market.markets
              .filter((marketDetail) => marketDetail.type === "SPOT")
              .sort((a, b) => parseFloat(b.volume24h) - parseFloat(a.volume24h))[0];
            return acc;
          }, {});

          dataResultCoins.value = topThreeCurrencyIds.map((currencyId) => {
            const coin = coinSearchData.find((market) => market.currencyId === currencyId);
            const marketDetail = marketDetailsMap[currencyId] || {};
            const contractDetails = getContractDetails(coin.contracts);
            return {
              ...coin,
              rank: coin.rank,
              dropstab_url: `https://dropstab.com/coins/${coin.slug}`,
              name: coin.name,
              change_1d_usd: coin.change['1D'].USD,
              price_usd: coin.price.USD,
              market_cap: coin.marketCap.USD,
              fdv: coin.fdv,
              exchange_link: marketDetail.link || '',
              exchange_image_link: marketDetail.exchangeImage || '',
              trading_pair: marketDetail.base && marketDetail.quote
                ? `${marketDetail.base}/${marketDetail.quote}`
                : '',
              explorer_url: contractDetails.explorerUrl,
              dexscreener_url: contractDetails.dexscreenerUrl,
              contract: contractDetails.contract,
              networkImage: contractDetails.networkImage,
              networkSymbol: contractDetails.networkSymbol,
            };
          });
        })
        .catch((error) => {
          console.error('Error fetching market details:', error);
        });
    })
    .catch((error) => {
      console.error('Error fetching coin search results:', error);
    });
  await Promise.all([fetchSearchResultsPromise, fetchCoinSearchResultsPromise]);
  toChangeLoaderVisible(false);
}, DEBOUNCE_TIME);
//Инициализировать Market Overview
const initMarketOverview = async () => {
  try {
    toChangeLoaderVisible(true);
    const data = await getMarketOverview();
    dataTrendingCoins.value = data.trending_coins;
    dataTopGainCoins.value = data.top_gain_coins;
    dataTopLossCoins.value = data.top_loss_coins;
    dataTrendingNftCollections.value = data.trending_nft_collections;
    toCheckScroll();
  } catch (error) {
    console.error('Error fetching market overview:', error);
  } finally {
    toChangeLoaderVisible(false);
  }
};
//Очистить поле для ввода
const clearField = () => query.value = '';
//Очистить состояние поиска
const resetSearchState = () => {
  clearField();
  outputResults.value = [];
  initMarketOverview();
};
//Сфокусироваться на поле ввода
const toFocus = () => inputField.value.focus();
//Закрыть попап с поиском
const toClose = () => store.dispatch('Dropdowns/removeTheDropdown', POPUP_NAME);
//Закрыть через кнопку Esc
const toCloseWithEsc = (e) => {
  if (e.key !== 'Escape' && e.keyCode !== 27) return;
  store.dispatch('Dropdowns/removeLastDropdown');
};
//Изменить статус инициализации
const toChangeLoaderVisible = (status) => isLoadingData.value = status;
//Определить, отображать ли градиент на границах области видимости
const toCheckScroll = () => {
  const box = scrollBox.value;
  if (!box) return;
  isScrollTop.value = (box.scrollTop <= 10) ? true : false;
  isScrollBottom.value = (box.scrollTop + box.clientHeight >= box.scrollHeight - 10) ? true : false;
}
//Вешаем слушатели событий
const toAddListeners = () => {
  window.addEventListener('keydown', toCloseWithEsc);
  window.addEventListener('resize', toCheckScroll);
}
//Удаляем слушатели событий
const toRemoveListeners = () => {
  window.removeEventListener('keydown', toCloseWithEsc);
  window.removeEventListener('resize', toCheckScroll);
}

//Загрузка следующей страницы
const loadNextPage = async () => {
  if (!nextPageUrl.value || isLoadingData.value) return;
  isLoadingNextPage.value = true;

  try {
    const {results, next} = await fetchNextPage(nextPageUrl.value);
    outputResults.value.push(...results);
    nextPageUrl.value = next;
  } catch (error) {
    console.error('Ошибка загрузки следующей страницы:', error);
  } finally {
    isLoadingNextPage.value = false;
  }
};


// Функция для получения деталей контракта
const getContractDetails = (contracts) => {
  if (!contracts || contracts.length === 0) {
    return {
      dexscreenerUrl: '',
      contract: '',
      networkImage: '',
      networkSymbol: '',
      explorerUrl: ''
    };
  }

  const ethContract = contracts.find(contract => contract.networkName === 'Ethereum');
  const solContract = contracts.find(contract => contract.networkName === 'Solana');

  if (ethContract) {
    return {
      dexscreenerUrl: ethContract.dexscreenerUrl,
      contract: ethContract.contract,
      networkImage: ethContract.networkImage,
      networkSymbol: ethContract.networkSymbol,
      explorerUrl: ethContract.explorerUrl
    };
  } else if (solContract) {
    return {
      dexscreenerUrl: solContract.dexscreenerUrl,
      contract: solContract.contract,
      networkImage: solContract.networkImage,
      networkSymbol: solContract.networkSymbol,
      explorerUrl: solContract.explorerUrl
    };
  } else {
    const firstContract = contracts[0];
    return {
      dexscreenerUrl: firstContract.dexscreenerUrl,
      contract: firstContract.contract,
      networkImage: firstContract.networkImage,
      networkSymbol: firstContract.networkSymbol,
      explorerUrl: firstContract.explorerUrl
    };
  }
};
</script>

<style lang="scss">
@import './styles/_conf';
</style>
